<template>
    <div v-if="isLoading" class="dd-loader" style="position: relative;">
      <spinner />
    </div>
    <div v-else-if="remoteUrl" style="width: 100%">
        <iframe
                type="text/html"
                allow="clipboard-write"
                :src="remoteUrl"
                style="width: inherit; height: 100%;">
        </iframe>
    </div>
    <div v-else-if="account" class="builder-empty-state" style="width: 100%">
        <div class="builder-empty" >
            <div>
                <img src="~dashboard/assets/images/lock.svg" alt="No Workflow" />
            </div>
        </div>
    </div>
</template>

<script>
import dstudioApi from 'dashboard/api/integrations/dstudio';
import {mapGetters} from "vuex";
import Spinner from 'shared/components/Spinner.vue';
import alertMixin from 'shared/mixins/alertMixin';

export default {
  components: {Spinner},
  mixins: [alertMixin],
  props: {
    accountId: [String, Number]
  },
  data() {
    return {
      remoteUrl: null,
      isLoading: true,
      // Set from postMessage by the embedded Design Studio (Angular) app when it has
      // unsaved chatbot changes in manual save mode.
      hasUnsavedChanges: false,
    };
  },
  mounted() {
    this.load();
    // The Design Studio runs in a cross-origin iframe; its own beforeunload/route guards do
    // NOT fire when WE (the host) navigate away and destroy the iframe. So it pushes its
    // unsaved state here and we guard our own navigation / page unload accordingly.
    window.addEventListener('message', this.onIframeMessage);
    window.addEventListener('beforeunload', this.onBeforeUnload);
  },
  beforeDestroy() {
    window.removeEventListener('message', this.onIframeMessage);
    window.removeEventListener('beforeunload', this.onBeforeUnload);
  },
  beforeRouteLeave(to, from, next) {
    if (this.hasUnsavedChanges) {
      next(window.confirm(this.$t('INTEGRATION_SETTINGS.UNSAVED_CHANGES')));
    } else {
      next();
    }
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount'
    }),
    account() {
      return this.getAccount(this.accountId)
    },
  },
  methods: {
    async load() {
      this.isLoading = true;
      try {
        const { data } = await dstudioApi.getLoginLink();
        this.remoteUrl = data?.url;
      } catch (error) {
        this.showAlert(this.$t('INTEGRATION_SETTINGS.API_ERROR_MESSAGE'));
      } finally {
        this.isLoading = false;
      }
    },
    onIframeMessage(event) {
      // Only trust messages coming from the embedded Design Studio iframe origin.
      if (!this.remoteUrl) return;
      let allowedOrigin;
      try {
        allowedOrigin = new URL(this.remoteUrl).origin;
      } catch (e) {
        return;
      }
      if (event.origin !== allowedOrigin) return;
      const data = event.data || {};
      if (data.source === 'dstudio' && data.type === 'unsavedChanges') {
        this.hasUnsavedChanges = !!data.dirty;
      }
    },
    onBeforeUnload(event) {
      // Guard a full host page reload/close while the editor has unsaved changes.
      if (this.hasUnsavedChanges) {
        event.preventDefault();
        event.returnValue = '';
        return '';
      }
      return undefined;
    },
  },
};
</script>

<style lang="scss" scoped>
.inbox-empty-state {
  height: 100%;
  overflow: auto;
}

.builder-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;

  div {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;

    img {
      margin: var(--space-normal);
      width: 10rem;
    }

    span {
      font-size: var(--font-size-small);
      font-weight: var(--font-weight-medium);
      text-align: center;
    }
  }
}

.builder-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
}
</style>
